import React, { useEffect, useState } from 'react';
import { listenAssignments, fetchSchoolCalendar } from './api';
import { weekNumberForDate, todayIso } from './calendarMath';
import {
  weekSnapshot, weeklyCompletion, attendanceStats, coverageWeeks, backlog,
} from './reporting';
import Bar from './Bar';

// "How is school going" on one screen. Every number here already existed
// somewhere — spread across Coverage, Trends, Attendance and Assignments —
// except the unplaced count, which no owner surface reported at all even
// though 17 tiles land in the pool every week.
//
// Deliberately a rollup, not a replacement: each block links into the tab that
// owns the detail, so nothing was removed to make room for this.
export default function DashboardTab({ familyData, student, onNavigate }) {
  const [assignments, setAssignments] = useState(null);
  const [calendar, setCalendar] = useState(undefined);

  useEffect(() => {
    if (!familyData?.id || !student?.studentEmail) return;
    let alive = true;
    const unsub = listenAssignments(familyData.id, student.studentEmail, rows => { if (alive) setAssignments(rows); });
    fetchSchoolCalendar(familyData.id).then(cal => { if (alive) setCalendar(cal ?? null); });
    return () => { alive = false; unsub(); };
  }, [familyData?.id, student?.studentEmail]);

  if (assignments === null || calendar === undefined) return <p className="settings-note">Loading…</p>;
  if (!calendar?.weeks?.length) return <p className="settings-note">No school calendar seeded yet — start on the Setup tab.</p>;

  const today = todayIso();
  const currentWeek = weekNumberForDate(calendar.weeks, today);
  const week = weekSnapshot(assignments, calendar, currentWeek);
  const pace = weeklyCompletion(assignments, { currentWeek, lookback: 8 });
  const attendance = attendanceStats(assignments, calendar);
  const { flaggedCount } = coverageWeeks(assignments, calendar, { currentWeek, before: 0, after: 3 });
  const late = backlog(assignments, today);

  const flagged = assignments.filter(a => a.status === 'flagged' || a.confusionFlag);
  const needsReview = assignments.filter(
    a => a.status === 'complete' && a.needsReview !== false && !a.reviewedByParent
  );

  const name = student?.preferred_name || student?.firstName || 'this student';
  const pctMinutes = week.plannedMinutes
    ? Math.round((week.completedMinutes / week.plannedMinutes) * 100)
    : 0;

  return (
    <div>
      {/* ── This week ───────────────────────────────────────────────── */}
      <div className="form-label" style={{ marginTop: 12 }}>This week · week {currentWeek}</div>
      <div className="db-card" style={{ padding: '14px 16px', marginBottom: 12 }}>
        {week.unplacedCount > 0 ? (
          <p className="settings-note" style={{ marginTop: 0 }}>
            <strong style={{ color: 'var(--bad)' }}>{week.unplacedCount} session{week.unplacedCount === 1 ? '' : 's'} still {week.unplacedCount === 1 ? 'has' : 'have'} no day.</strong>{' '}
            {name} places these on This week; until then they aren't on any day and don't count toward attendance.
          </p>
        ) : (
          <p className="settings-note" style={{ marginTop: 0 }}>
            Everything this week has a day. {week.placedCount} session{week.placedCount === 1 ? '' : 's'} scheduled.
          </p>
        )}

        <div className="db-meter" style={{ marginTop: 10 }}>
          <div className="db-meter-track">
            <div className="db-meter-fill" style={{ width: `${pctMinutes}%` }} />
          </div>
          <span className="db-meter-label">{week.completedMinutes}m / {week.plannedMinutes}m</span>
        </div>

        <div className="db-block-meta">
          <span>{week.completeCount}/{week.total} done</span>
          <span>{week.floorDaysMet}/{week.schoolDays} days met the floor</span>
          {late.total > 0 && <span style={{ color: 'var(--bad)' }}>{late.total} overdue</span>}
        </div>
        <Link onClick={() => onNavigate?.('board')}>See the board {name} sees ›</Link>
      </div>

      {/* ── Needs me ────────────────────────────────────────────────── */}
      <div className="form-label">Needs me</div>
      <div className="db-card" style={{ padding: '14px 16px', marginBottom: 12 }}>
        {flagged.length === 0 && needsReview.length === 0 ? (
          <p className="settings-note" style={{ marginTop: 0 }}>Nothing flagged, nothing waiting on a review.</p>
        ) : (
          <>
            {flagged.length > 0 && (
              <>
                <p className="settings-note" style={{ marginTop: 0 }}>
                  🚩 {flagged.length} flagged as stuck
                </p>
                <ul className="db-items" style={{ padding: 0 }}>
                  {flagged.slice(0, 5).map(a => (
                    <li key={a.id} className="db-item" style={{ '--bc': 'var(--bad)' }}>
                      <div className="db-item-body">
                        <div className="db-item-title">{a.title}</div>
                        <div className="db-item-sub">
                          {a.studentQuestion
                            ? `“${a.studentQuestion}”`
                            : 'No note left — worth asking.'}
                        </div>
                      </div>
                      <div className="db-item-meta"><span className="db-mins">{a.date || 'no day'}</span></div>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {needsReview.length > 0 && (
              <p className="settings-note">{needsReview.length} finished item{needsReview.length === 1 ? '' : 's'} waiting for a look.</p>
            )}
          </>
        )}
        <Link onClick={() => onNavigate?.('assignments')}>Open Assignments ›</Link>
      </div>

      {/* ── On the record ───────────────────────────────────────────── */}
      <div className="form-label">On the record</div>
      <div className="db-card" style={{ padding: '14px 16px', marginBottom: 12, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        <Stat value={`${attendance.totalCounted}`} of={`/ ${calendar.totalInstructionalDays}`} label="instructional days" />
        <Stat value={attendance.totalHours.toFixed(0)} label="hours completed" />
        <div style={{ flexBasis: '100%' }}>
          <Link onClick={() => onNavigate?.('attendance')}>Open Attendance ›</Link>
        </div>
      </div>

      {/* ── Pace ────────────────────────────────────────────────────── */}
      <div className="form-label">Pace · last {pace.length} week{pace.length === 1 ? '' : 's'}</div>
      <div className="db-card" style={{ padding: '14px 16px', marginBottom: 12 }}>
        {pace.length === 0 ? (
          <p className="settings-note" style={{ marginTop: 0 }}>No completed weeks yet.</p>
        ) : (
          pace.map(w => (
            <Bar key={w.weekNumber}
              label={`Week ${w.weekNumber}${w.weekNumber === currentWeek ? ' (this week)' : ''}`}
              pct={w.pct} sublabel={`${w.complete}/${w.total}`} />
          ))
        )}
        <Link onClick={() => onNavigate?.('trends')}>Open Trends ›</Link>
      </div>

      {/* ── Gaps ────────────────────────────────────────────────────── */}
      <div className="form-label">Gaps ahead</div>
      <div className="db-card" style={{ padding: '14px 16px', marginBottom: 20 }}>
        <p className="settings-note" style={{ marginTop: 0 }}>
          {flaggedCount === 0
            ? 'Every school day in the next few weeks has must-do work on it.'
            : `${flaggedCount} day(s) in the next few weeks have no work, or nothing that counts toward the floor.`}
        </p>
        <Link onClick={() => onNavigate?.('coverage')}>Open Coverage ›</Link>
      </div>
    </div>
  );
}

function Stat({ value, of, label }) {
  return (
    <div>
      <div style={{ fontSize: 28, fontFamily: 'var(--font-display)' }}>
        {value}{of && <span style={{ fontSize: 16, color: 'var(--ink-3)' }}> {of}</span>}
      </div>
      <div className="settings-note" style={{ marginTop: 0 }}>{label}</div>
    </div>
  );
}

function Link({ onClick, children }) {
  return (
    <button type="button" className="db-detail-toggle" style={{ padding: '8px 0 0' }} onClick={onClick}>
      {children}
    </button>
  );
}
